import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Plus,
  Trash2,
  ChevronRight,
  ChevronDown,
  Search,
  Zap,
  Filter,
  AlertTriangle,
  CheckCircle2,
} from 'lucide-react';
import {
  StrategyConfig,
  StrategyFilter,
  StrategyFilterField,
  StrategyCategory,
  AlertTypeDefinition,
  STRATEGY_FILTER_FIELDS,
  STRATEGY_FILTER_FIELDS_GROUPED,
  STRATEGY_CATEGORIES,
  FILTER_OPERATORS,
  strategyApiService,
} from '@/services/strategyConfigService';
import type { FilterOperator } from '@/services/scannerService';

// ── Props ──────────────────────────────────────────────────

interface StrategyBuilderProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (config: StrategyConfig) => void;
  editStrategy?: StrategyConfig | null;
}

// ── Component ──────────────────────────────────────────────

export const StrategyBuilder: React.FC<StrategyBuilderProps> = ({
  open,
  onOpenChange,
  onSave,
  editStrategy,
}) => {
  const isEdit = !!editStrategy;

  // ── Form state ──
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<StrategyCategory>('Custom');
  const [selectedAlerts, setSelectedAlerts] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState<StrategyFilter[]>([]);
  const [maxResults, setMaxResults] = useState(50);
  const [symbolList, setSymbolList] = useState('');

  // ── Alert catalog state ──
  const [alertCatalog, setAlertCatalog] = useState<Record<string, AlertTypeDefinition[]>>({});
  const [alertSearch, setAlertSearch] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [catalogLoading, setCatalogLoading] = useState(false);

  // ── Active tab ──
  const [activeTab, setActiveTab] = useState<'alerts' | 'filters' | 'settings'>('alerts');

  // ── Load alert catalog ──
  useEffect(() => {
    if (!open) return;
    setCatalogLoading(true);
    strategyApiService
      .getAlertCatalogGrouped()
      .then(setAlertCatalog)
      .catch(console.error)
      .finally(() => setCatalogLoading(false));
  }, [open]);

  // ── Reset form when dialog opens ──
  useEffect(() => {
    if (!open) return;
    if (editStrategy) {
      setName(editStrategy.name);
      setDescription(editStrategy.description);
      setCategory(editStrategy.category);
      setSelectedAlerts(new Set(editStrategy.alerts));
      setFilters(editStrategy.filters);
      setMaxResults(editStrategy.maxResults);
      setSymbolList(editStrategy.symbolList.join(', '));
    } else {
      setName('');
      setDescription('');
      setCategory('Custom');
      setSelectedAlerts(new Set());
      setFilters([]);
      setMaxResults(50);
      setSymbolList('');
    }
    setActiveTab('alerts');
    setAlertSearch('');
  }, [open, editStrategy]);

  // ── Alert selection ──
  const toggleAlert = useCallback((code: string) => {
    setSelectedAlerts(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }, []);

  const toggleCategory = useCallback((cat: string, codes: string[]) => {
    setSelectedAlerts(prev => {
      const next = new Set(prev);
      const allSelected = codes.every(c => next.has(c));
      codes.forEach(c => allSelected ? next.delete(c) : next.add(c));
      return next;
    });
  }, []);

  const toggleExpand = useCallback((cat: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  }, []);

  // ── Filtered alert catalog ──
  const filteredCatalog = useMemo(() => {
    if (!alertSearch.trim()) return alertCatalog;
    const q = alertSearch.toLowerCase();
    const result: Record<string, AlertTypeDefinition[]> = {};
    for (const [cat, alerts] of Object.entries(alertCatalog)) {
      const filtered = alerts.filter(
        a =>
          a.name.toLowerCase().includes(q) ||
          a.code.toLowerCase().includes(q) ||
          a.description.toLowerCase().includes(q)
      );
      if (filtered.length > 0) result[cat] = filtered;
    }
    return result;
  }, [alertCatalog, alertSearch]);

  // ── Filter CRUD ──
  const addFilter = useCallback(() => {
    setFilters(prev => [
      ...prev,
      { field: 'Price' as StrategyFilterField, operator: 'GreaterThan' as FilterOperator, value: 0 },
    ]);
  }, []);

  const removeFilter = useCallback((index: number) => {
    setFilters(prev => prev.filter((_, i) => i !== index));
  }, []);

  const updateFilter = useCallback((index: number, updates: Partial<StrategyFilter>) => {
    setFilters(prev => prev.map((f, i) => (i === index ? { ...f, ...updates } : f)));
  }, []);

  // ── Save ──
  const handleSave = useCallback(() => {
    if (!name.trim()) return;
    const symbols = symbolList
      .split(/[,;\s]+/)
      .map(s => s.trim().toUpperCase())
      .filter(Boolean);

    const config: StrategyConfig = {
      id: editStrategy?.id ?? `custom-${Date.now()}`,
      name: name.trim(),
      description: description.trim(),
      category,
      alerts: Array.from(selectedAlerts),
      filters,
      exchanges: [],
      symbolList: symbols,
      columns: [],
      maxResults,
      isPreset: false,
      isActive: true,
      createdAt: editStrategy?.createdAt ?? new Date().toISOString(),
      lastUpdated: new Date().toISOString(),
    };
    onSave(config);
    onOpenChange(false);
  }, [name, description, category, selectedAlerts, filters, maxResults, symbolList, editStrategy, onSave, onOpenChange]);

  // ── Summary counts ──
  const alertCount = selectedAlerts.size;
  const filterCount = filters.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-yellow-500" />
            {isEdit ? 'Edit Strategy' : 'Create Strategy'}
          </DialogTitle>
          <DialogDescription>
            Select alert types (OR — any fires) and window filters (AND — all must pass)
          </DialogDescription>
        </DialogHeader>

        {/* ── Name / Category row ── */}
        <div className="grid grid-cols-[1fr_160px] gap-3 px-1">
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Name</label>
            <input
              className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="My Strategy"
              value={name}
              onChange={e => setName(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Category</label>
            <Select value={category} onValueChange={v => setCategory(v as StrategyCategory)}>
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STRATEGY_CATEGORIES.map(c => (
                  <SelectItem key={c.value} value={c.value}>
                    <span className="flex items-center gap-1.5">
                      <span>{c.icon}</span>
                      {c.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="px-1">
          <input
            className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            placeholder="Strategy description (optional)"
            value={description}
            onChange={e => setDescription(e.target.value)}
          />
        </div>

        {/* ── Tab bar ── */}
        <div className="flex border-b border-border px-1">
          <button
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'alerts'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            onClick={() => setActiveTab('alerts')}
          >
            <span className="flex items-center gap-1.5">
              <Zap className="h-3.5 w-3.5" />
              Alerts
              {alertCount > 0 && (
                <Badge variant="default" className="h-5 px-1.5 text-[10px] rounded-full">
                  {alertCount}
                </Badge>
              )}
            </span>
          </button>
          <button
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'filters'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            onClick={() => setActiveTab('filters')}
          >
            <span className="flex items-center gap-1.5">
              <Filter className="h-3.5 w-3.5" />
              Filters
              {filterCount > 0 && (
                <Badge variant="default" className="h-5 px-1.5 text-[10px] rounded-full">
                  {filterCount}
                </Badge>
              )}
            </span>
          </button>
          <button
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'settings'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            onClick={() => setActiveTab('settings')}
          >
            Settings
          </button>
        </div>

        {/* ── Tab content ── */}
        <div className="flex-1 overflow-y-auto px-1 min-h-[350px]">
          {/* ═══ ALERTS TAB ═══ */}
          {activeTab === 'alerts' && (
            <div className="space-y-3 py-2">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <input
                  className="w-full h-9 pl-9 pr-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="Search alerts..."
                  value={alertSearch}
                  onChange={e => setAlertSearch(e.target.value)}
                />
              </div>

              <p className="text-xs text-muted-foreground">
                Select alert types — a stock matches if <strong>ANY</strong> selected alert fires.
                Leave empty for a pure filter-based strategy.
              </p>

              {catalogLoading ? (
                <div className="text-sm text-muted-foreground text-center py-8">Loading alert catalog...</div>
              ) : Object.keys(filteredCatalog).length === 0 ? (
                <div className="text-sm text-muted-foreground text-center py-8">No alerts found</div>
              ) : (
                <div className="space-y-1">
                  {Object.entries(filteredCatalog).map(([cat, alerts]) => {
                    const expanded = expandedCategories.has(cat) || alertSearch.trim().length > 0;
                    const catCodes = alerts.map(a => a.code);
                    const selectedInCat = catCodes.filter(c => selectedAlerts.has(c)).length;
                    const allSelected = selectedInCat === catCodes.length;

                    return (
                      <div key={cat} className="border border-border rounded-md overflow-hidden">
                        {/* Category header */}
                        <div
                          className="flex items-center gap-2 px-3 py-2 bg-muted/40 cursor-pointer hover:bg-muted/60 transition-colors select-none"
                          onClick={() => toggleExpand(cat)}
                        >
                          {expanded ? (
                            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                          ) : (
                            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                          )}
                          <span className="text-sm font-medium flex-1">{cat}</span>
                          {selectedInCat > 0 && (
                            <Badge variant="default" className="h-5 px-1.5 text-[10px]">
                              {selectedInCat}/{catCodes.length}
                            </Badge>
                          )}
                          <button
                            className="text-[10px] text-muted-foreground hover:text-foreground px-1.5 py-0.5 rounded border border-border"
                            onClick={e => {
                              e.stopPropagation();
                              toggleCategory(cat, catCodes);
                            }}
                          >
                            {allSelected ? 'Deselect All' : 'Select All'}
                          </button>
                        </div>

                        {/* Alert list */}
                        {expanded && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-0">
                            {alerts.map(alert => {
                              const isSelected = selectedAlerts.has(alert.code);
                              return (
                                <div
                                  key={alert.code}
                                  className={`flex items-start gap-2 px-3 py-1.5 cursor-pointer transition-colors border-b border-border/40 last:border-b-0 ${
                                    isSelected
                                      ? 'bg-primary/10'
                                      : 'hover:bg-muted/30'
                                  }`}
                                  onClick={() => toggleAlert(alert.code)}
                                >
                                  <div
                                    className={`mt-0.5 h-4 w-4 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${
                                      isSelected
                                        ? 'bg-primary border-primary'
                                        : 'border-muted-foreground/40'
                                    }`}
                                  >
                                    {isSelected && (
                                      <CheckCircle2 className="h-3 w-3 text-primary-foreground" />
                                    )}
                                  </div>
                                  <div className="min-w-0">
                                    <div className="text-xs font-medium truncate">
                                      {alert.name}
                                      <span className="ml-1.5 text-[10px] text-muted-foreground font-mono">
                                        {alert.code}
                                      </span>
                                    </div>
                                    <div className="text-[10px] text-muted-foreground/70 truncate">
                                      {alert.description}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ═══ FILTERS TAB ═══ */}
          {activeTab === 'filters' && (
            <div className="space-y-3 py-2">
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">
                  Window-specific filters — <strong>ALL</strong> must pass for a stock to appear.
                </p>
                <Button variant="outline" size="sm" onClick={addFilter} className="h-7 text-xs">
                  <Plus className="h-3 w-3 mr-1" /> Add Filter
                </Button>
              </div>

              {filters.length === 0 ? (
                <div className="text-center py-8 text-sm text-muted-foreground/60">
                  <Filter className="h-8 w-8 mx-auto mb-2 opacity-30" />
                  No filters — all matching alerts will appear
                </div>
              ) : (
                <div className="space-y-2">
                  {filters.map((filter, i) => {
                    const meta = STRATEGY_FILTER_FIELDS.find(f => f.field === filter.field);
                    return (
                      <div
                        key={i}
                        className="flex items-center gap-2 bg-muted/30 rounded-md p-2 border border-border/50"
                      >
                        {/* Field */}
                        <Select
                          value={filter.field}
                          onValueChange={v =>
                            updateFilter(i, { field: v as StrategyFilterField })
                          }
                        >
                          <SelectTrigger className="h-8 w-[160px] text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(STRATEGY_FILTER_FIELDS_GROUPED).map(
                              ([group, fields]) => (
                                <React.Fragment key={group}>
                                  <div className="px-2 py-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                                    {group}
                                  </div>
                                  {fields.map(f => (
                                    <SelectItem key={f.field} value={f.field}>
                                      {f.label}
                                    </SelectItem>
                                  ))}
                                </React.Fragment>
                              )
                            )}
                          </SelectContent>
                        </Select>

                        {/* Operator */}
                        <Select
                          value={filter.operator}
                          onValueChange={v =>
                            updateFilter(i, { operator: v as FilterOperator })
                          }
                        >
                          <SelectTrigger className="h-8 w-[90px] text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {FILTER_OPERATORS.map(op => (
                              <SelectItem key={op.value} value={op.value}>
                                {op.symbol} {op.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        {/* Value */}
                        <input
                          type="number"
                          className="h-8 w-[100px] px-2 rounded-md border border-input bg-background text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                          value={filter.value}
                          onChange={e =>
                            updateFilter(i, {
                              value: parseFloat(e.target.value) || 0,
                            })
                          }
                          step={meta?.step ?? 0.01}
                          placeholder="Value"
                        />

                        {/* Value2 for Between */}
                        {filter.operator === 'Between' && (
                          <>
                            <span className="text-xs text-muted-foreground">to</span>
                            <input
                              type="number"
                              className="h-8 w-[100px] px-2 rounded-md border border-input bg-background text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                              value={filter.value2 ?? ''}
                              onChange={e =>
                                updateFilter(i, {
                                  value2: parseFloat(e.target.value) || 0,
                                })
                              }
                              step={meta?.step ?? 0.01}
                              placeholder="Max"
                            />
                          </>
                        )}

                        {meta?.unit && (
                          <span className="text-[10px] text-muted-foreground">{meta.unit}</span>
                        )}

                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 shrink-0 ml-auto"
                          onClick={() => removeFilter(i)}
                        >
                          <Trash2 className="h-3 w-3 text-red-400" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Quick-add filter presets */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                  Quick Add
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {[
                    { label: 'Price > $1', field: 'Price', op: 'GreaterThan', val: 1 },
                    { label: 'Price < $20', field: 'Price', op: 'LessThan', val: 20 },
                    { label: 'RVol > 2', field: 'RelativeVolume', op: 'GreaterThan', val: 2 },
                    { label: 'Vol > 100K', field: 'Volume', op: 'GreaterThan', val: 100000 },
                    { label: 'Change > 5%', field: 'ChangePercent', op: 'GreaterThan', val: 5 },
                    { label: 'Float < 20M', field: 'Float', op: 'LessThan', val: 20000000 },
                    { label: 'MCap < 2B', field: 'MarketCap', op: 'LessThan', val: 2000000000 },
                    { label: 'Gap > 3%', field: 'GapPercent', op: 'GreaterThan', val: 3 },
                  ].map(preset => (
                    <Badge
                      key={preset.label}
                      variant="outline"
                      className="cursor-pointer hover:bg-primary/10 text-[10px]"
                      onClick={() =>
                        setFilters(prev => [
                          ...prev,
                          {
                            field: preset.field as StrategyFilterField,
                            operator: preset.op as FilterOperator,
                            value: preset.val,
                          },
                        ])
                      }
                    >
                      <Plus className="h-2.5 w-2.5 mr-0.5" />
                      {preset.label}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ═══ SETTINGS TAB ═══ */}
          {activeTab === 'settings' && (
            <div className="space-y-4 py-2">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Max Results</label>
                <input
                  type="number"
                  className="w-[120px] h-9 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  value={maxResults}
                  onChange={e => setMaxResults(parseInt(e.target.value) || 50)}
                  min={1}
                  max={200}
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">
                  Symbol List (optional — comma separated)
                </label>
                <input
                  className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="AAPL, MSFT, TSLA  (leave empty for all subscribed symbols)"
                  value={symbolList}
                  onChange={e => setSymbolList(e.target.value)}
                />
                <p className="text-[10px] text-muted-foreground">
                  If empty, the strategy evaluates all symbols the server is tracking.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* ── Summary bar ── */}
        <div className="flex items-center gap-3 px-1 py-2 border-t border-border text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <Zap className="h-3 w-3" />
            <span>{alertCount} alert{alertCount !== 1 ? 's' : ''}</span>
          </div>
          <div className="flex items-center gap-1">
            <Filter className="h-3 w-3" />
            <span>{filterCount} filter{filterCount !== 1 ? 's' : ''}</span>
          </div>
          {alertCount === 0 && filterCount === 0 && (
            <div className="flex items-center gap-1 text-yellow-500">
              <AlertTriangle className="h-3 w-3" />
              <span>Strategy has no alerts or filters</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!name.trim()}>
            {isEdit ? 'Save Changes' : 'Create Strategy'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default StrategyBuilder;
